/**
 * RequestForm.jsx — Inquiry form for classes that are scheduled by appointment
 *
 * Used for two class types that don't have a fixed public date/time:
 *   - 'wheel'  Wheel Throwing (Home Studio) — private 1-on-1, scheduling by inquiry
 *   - 'party'  Pottery Party (Luz y Tierra) — private group event, scheduling by inquiry
 *
 * Flow:
 *   1. Customer fills out the form and submits
 *   2. Firebase Cloud Function sends an email to the studio owner
 *   3. Customer sees a confirmation message
 *   4. Studio owner replies directly to the customer to agree on date/time
 *
 * Props:
 *   classType  {'wheel'|'party'}  — determines which fields are shown
 *   className  {string}            — displayed in the form header and email subject
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useInquiryForm } from '../services/firestore';

// Hardcoded defaults — used when no Firestore config exists yet
const DEFAULTS = {
  'pottery-party': {
    title: 'Request a Private Party',
    footerNote:
      "We'll respond within 1–2 business days to confirm availability, pricing, and logistics.",
    fields: {
      commPref: true,
      guests: true,
      eventType: true,
      ceramicType: true,
      preferredSchedule: true,
      notes: true,
    },
  },
  'wheel-throwing': {
    title: 'Request a Private Session',
    footerNote:
      "We'll respond within 1–2 business days to confirm availability and share a booking link.",
    fields: { commPref: true, preferredSchedule: true, notes: true },
  },
};

const inputStyle = {
  width: '100%',
  border: 'none',
  borderBottom: '1px solid #ddd8cc',
  background: 'transparent',
  padding: '11px 0',
  fontSize: '0.9rem',
  outline: 'none',
  fontFamily: "'Raleway', sans-serif",
  fontWeight: 700,
  color: '#1a1208',
  boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23aaa'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 4px center',
  paddingRight: 20,
};

const labelStyle = {
  display: 'block',
  fontFamily: "'Raleway', sans-serif",
  fontWeight: 700,
  fontSize: '0.62rem',
  letterSpacing: '0.15em',
  color: '#9C7A3C',
  textTransform: 'uppercase',
  marginBottom: 2,
};

const fieldWrap = { marginBottom: 20 };

const twoCol = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 20,
};

export default function RequestForm({ formId, className }) {
  const isParty = formId === 'pottery-party';
  const { form: firestoreConfig } = useInquiryForm(formId);
  const defaults = DEFAULTS[formId] || DEFAULTS['wheel-throwing'];
  const config = {
    title: firestoreConfig?.title ?? defaults.title,
    footerNote: firestoreConfig?.footerNote ?? defaults.footerNote,
    fields: { ...defaults.fields, ...(firestoreConfig?.fields ?? {}) },
  };

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    commPref: '',
    guests: '',
    eventType: '',
    ceramicType: '',
    preferredSchedule: '',
    notes: '',
  });

  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  // Pre-warm the submitInquiry function so the real submit is instant.
  useEffect(() => {
    httpsCallable(functions, 'submitInquiry')({ warmup: true }).catch(() => {});
  }, []);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const submitInquiry = httpsCallable(functions, 'submitInquiry');
      await submitInquiry({ ...form, classType: formId, className });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div style={{ padding: '48px 40px', textAlign: 'center' }}>
        <div style={{ fontSize: '1.4rem', marginBottom: 16, color: '#9C7A3C' }}>
          ✦
        </div>
        <h3
          style={{
            fontFamily: "'Titan One', sans-serif",
            fontWeight: 400,
            fontSize: '1.6rem',
            letterSpacing: '-0.01em',
            color: '#1a1208',
            marginBottom: 12,
          }}
        >
          INQUIRY RECEIVED
        </h3>
        <p
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
            color: '#1a1208',
            lineHeight: 1.7,
            maxWidth: 360,
            margin: '0 auto',
          }}
        >
          Thank you, {form.firstName}. We&apos;ll review your request and reach
          out within 1–2 business days to confirm details and share a booking
          link.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '36px 40px' }}>
      <div
        style={{
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#9C7A3C',
          marginBottom: 24,
        }}
      >
        {config.title}
      </div>

      {/* Contact information */}
      <div style={{ ...twoCol, marginBottom: 20 }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>First Name *</label>
          <input
            style={inputStyle}
            value={form.firstName}
            onChange={set('firstName')}
            required
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Last Name *</label>
          <input
            style={inputStyle}
            value={form.lastName}
            onChange={set('lastName')}
            required
          />
        </div>
      </div>

      <div style={{ ...twoCol, marginBottom: 20 }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Phone *</label>
          <input
            style={inputStyle}
            type="tel"
            value={form.phone}
            onChange={set('phone')}
            required
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Email *</label>
          <input
            style={inputStyle}
            type="email"
            value={form.email}
            onChange={set('email')}
            required
          />
        </div>
      </div>

      {/* Communication preference */}
      {config.fields.commPref && (
        <div style={fieldWrap}>
          <label style={labelStyle}>Preferred Contact Method</label>
          <select
            style={selectStyle}
            value={form.commPref}
            onChange={set('commPref')}
          >
            <option value="">Select one…</option>
            <option>Text</option>
            <option>Email</option>
          </select>
        </div>
      )}

      {/* Party-specific fields */}
      {isParty && (config.fields.guests || config.fields.eventType) && (
        <div style={{ ...twoCol, marginBottom: 20 }}>
          {config.fields.guests && (
            <div style={fieldWrap}>
              <label style={labelStyle}>Number of Guests *</label>
              <input
                style={inputStyle}
                type="number"
                min={2}
                max={25}
                value={form.guests}
                onChange={set('guests')}
                required
                placeholder="Luz y Tierra min. 12 · Home Studio min. 4 · Your Location min. 10"
              />
            </div>
          )}
          {config.fields.eventType && (
            <div style={fieldWrap}>
              <label style={labelStyle}>Type of Event</label>
              <select
                style={selectStyle}
                value={form.eventType}
                onChange={set('eventType')}
              >
                <option value="">Select one…</option>
                <option>Birthday</option>
                <option>Bachelorette / Bridal</option>
                <option>Girl&apos;s Night</option>
                <option>Date Night</option>
                <option>Corporate / Team Building</option>
                <option>Holiday Gathering</option>
                <option>Other Celebration</option>
              </select>
            </div>
          )}
        </div>
      )}

      {isParty && config.fields.ceramicType && (
        <div style={fieldWrap}>
          <label style={labelStyle}>Ceramic Style</label>
          <select
            style={selectStyle}
            value={form.ceramicType}
            onChange={set('ceramicType')}
          >
            <option value="">Select one…</option>
            <option>Mug</option>
            <option>Bowl</option>
            <option>Planter</option>
            <option>Custom</option>
          </select>
        </div>
      )}

      {config.fields.preferredSchedule && (
        <div style={fieldWrap}>
          <label style={labelStyle}>Preferred Schedule</label>
          <input
            style={inputStyle}
            value={form.preferredSchedule}
            onChange={set('preferredSchedule')}
            placeholder={
              isParty
                ? 'e.g. Saturday afternoon in July, weekday evenings…'
                : 'e.g. Weekend mornings, Tuesday afternoons…'
            }
          />
        </div>
      )}

      {config.fields.notes && (
        <div style={{ ...fieldWrap, marginBottom: 32 }}>
          <label style={labelStyle}>Additional Notes</label>
          <textarea
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 72,
              paddingTop: 8,
            }}
            value={form.notes}
            onChange={set('notes')}
            placeholder={
              isParty
                ? "Location preference, questions, or anything else you'd like us to know\u2026"
                : "Goals, questions, anything else you'd like us to know\u2026"
            }
          />
        </div>
      )}

      <button
        type="submit"
        className="book-btn"
        disabled={status === 'sending'}
        style={{ opacity: status === 'sending' ? 0.6 : 1 }}
      >
        {status === 'sending' ? 'Sending…' : 'Send Inquiry'}
      </button>

      {status === 'error' && (
        <p
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            color: '#c0392b',
            fontSize: '0.85rem',
            marginTop: 12,
            textAlign: 'center',
          }}
        >
          Something went wrong. Please email us directly at{' '}
          <a
            href="mailto:hello@virisclayworks.com"
            style={{ color: '#c0392b' }}
          >
            hello@virisclayworks.com
          </a>
        </p>
      )}

      <p
        style={{
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          color: '#9C7A3C',
          fontSize: '0.75rem',
          marginTop: 16,
          textAlign: 'center',
        }}
      >
        {config.footerNote}
      </p>
    </form>
  );
}
